import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoModule } from "@ngneat/transloco";

export interface RadioCardItem<T = unknown> {
  value: T;
  label: string;
  description?: string;
  tooltip?: string;
  icon?: string;
  iconSrc?: string;
}

let nextGroupId = 0;

@Component({
  selector: "app-radio-card-group",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbTooltipModule, TranslocoModule],
  templateUrl: "./radio-card-group.component.html",
  styleUrls: ["./radio-card-group.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioCardGroupComponent<T = unknown> {
  readonly control = input.required<FormControl<T | null>>();
  readonly items = input.required<RadioCardItem<T>[]>();
  readonly groupName = input<string>("");
  readonly title = input<string>("");
  readonly titleTooltip = input<string>("");
  readonly disabled = input<boolean>(false);
  readonly translateLabels = input<boolean>(true);
  /** Shows a lock icon next to the title to signal the selector is read-only for this merchant. */
  readonly locked = input<boolean>(false);

  private readonly instanceId = ++nextGroupId;

  protected readonly effectiveGroupName = computed(() => this.groupName() || `radio-card-group-${this.instanceId}`);

  // FormControl.value is not reactive on its own — sync it into a signal so that
  // OnPush change detection re-evaluates "is-selected" bindings when the parent
  // patches the form (e.g. after async load).
  protected readonly currentValue = signal<T | null>(null);

  constructor() {
    effect(
      (onCleanup) => {
        const ctrl = this.control();
        this.currentValue.set(ctrl.value);
        const sub = ctrl.valueChanges.subscribe((value: T | null) => this.currentValue.set(value));
        onCleanup(() => sub.unsubscribe());
      },
      { allowSignalWrites: true }
    );
  }
}
